/**
 * Loads the PDP "More From <brand>" shelf from the brand-filtered collection
 * view (templates/collection.brand-row.liquid) instead of from a scan of
 * `collections.all.products` in Liquid.
 *
 * The heading and the "view all" link are server-rendered — the PDP knows its
 * own brand — so only the cards are fetched. The shelf stays hidden until it
 * has at least one sibling product, which is the earliest anything here can
 * know: a brand with a single product yields a response containing only the
 * product being viewed.
 */
const parser = new DOMParser();

/**
 * Response text keyed by request URL.
 *
 * Every member of a combined listing shares a brand, so switching print re-runs
 * the load against a URL that has already been fetched — measured at 205KB a
 * time, uncached, once per switch. The cards are identical between siblings;
 * only which one is excluded changes, and that is decided here rather than by
 * the server. Kept as text because the parsed document is mutated per use.
 * @type {Map<string, string>}
 */
const responseCache = new Map();

class PdpBrandRow extends HTMLElement {
  /** @type {IntersectionObserver} */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.unobserve(this);
      this.#load();
    },
    { rootMargin: '0px 0px 600px 0px' }
  );

  /**
   * A combined-listing variant switch re-renders this section for the sibling
   * product and morphs it in place (assets/variant-picker.js
   * #postFetchAncillarySections). Morph keeps the same element — so
   * `connectedCallback` does not run again — while stripping `data-loaded`,
   * restoring `[hidden]` and emptying the host. Without this the shelf would
   * disappear on the first print switch and never come back.
   *
   * `data-exclude-handle` is server-rendered and always changes when the product
   * does (the ancillary refresh only runs when a new product loads), and nothing
   * here ever writes it, so it is a signal that cannot feed back on itself.
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver(() => this.#rearm());

  /** @type {AbortController | null} */
  #activeFetch = null;

  /** Guards against a superseded fetch injecting the previous product's cards. */
  #requestId = 0;

  connectedCallback() {
    this.#mutationObserver.observe(this, {
      attributes: true,
      attributeFilter: ['data-url', 'data-exclude-handle'],
    });

    if (this.dataset.loaded === 'true') return;
    this.#intersectionObserver.observe(this);
  }

  disconnectedCallback() {
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
    this.#activeFetch?.abort();
  }

  #rearm() {
    this.#requestId += 1;
    this.#activeFetch?.abort();
    delete this.dataset.loaded;
    delete this.dataset.error;
    // Re-observing fires straight away when the row is already on screen, and
    // stays lazy when the shopper switched print further up the page.
    this.#intersectionObserver.observe(this);
  }

  async #load() {
    const { url, excludeHandle, sectionId } = this.dataset;
    const host = this.querySelector('[data-brand-row-host]');

    if (!url || !host) return;
    this.dataset.loaded = 'true';

    const requestId = this.#requestId;

    try {
      let text = responseCache.get(url);

      if (text == null) {
        this.#activeFetch = new AbortController();
        const response = await fetch(url, { signal: this.#activeFetch.signal });
        if (!response.ok) throw new Error(`Server returned ${response.status}`);

        text = await response.text();
        responseCache.set(url, text);
      }

      if (requestId !== this.#requestId) return;

      const carousel = parser.parseFromString(text, 'text/html').querySelector('.resource-list__carousel');
      if (!carousel) throw new Error('Response carried no carousel');

      // Only this row's own slides. Every product card contains a media
      // gallery, which is itself a slideshow — a descendant query for
      // `slideshow-slide` matches those too (61 of them for a row of 9), so
      // scope to the direct children of the first `slideshow-slides`, which in
      // tree order is always this row's (it opens before the cards inside it).
      const scroller = carousel.querySelector('slideshow-slides');
      const slides = scroller ? [...scroller.children].filter((el) => el.matches('slideshow-slide')) : [];

      if (!slides.length) throw new Error('Response carried no slides');

      // A parsed document is inert — custom elements do not upgrade in it — so
      // slides can be dropped here and <slideshow-component> only ever
      // initialises once, against the final list. Slides keep their original
      // `--slide-N` timeline names, which the container still scopes, so
      // nothing has to be renumbered after a removal.
      const max = Number(this.dataset.max) || 8;
      let kept = 0;

      for (const slide of slides) {
        // Scoped to the item wrapper: the wishlist button inside each card also
        // carries `data-product-handle`, so an unscoped query is one markup
        // change away from reading the wrong element.
        const handle = slide.querySelector('.resource-list__item')?.dataset.productHandle;

        if (kept >= max || (excludeHandle && handle === excludeHandle)) {
          slide.remove();
        } else {
          kept += 1;
        }
      }

      if (kept === 0) throw new Error('Brand has no other products');

      // Retarget `sizes` before these images are ever in a document that
      // fetches them.
      //
      // Two things are wrong with what the server sends. `card-gallery.liquid`
      // can only narrow `sizes` from a section's `columns` / `product_card_size`
      // settings, and this row's section has neither, so the cards ship the
      // crude `50vw` fallback for a slot that renders at ~256px. And the
      // `auto,` prefix it prepends is only valid on `loading="lazy"` images —
      // on an eager one the whole attribute is discarded and the browser
      // assumes `100vw`, which is how a 250px slot ended up pulling the 1118px
      // candidate (measured: 8 of 11 row images).
      //
      // A plain list, no `auto`, is valid either way and needs no layout to
      // resolve — which matters here because these are injected, so `auto`
      // could not be relied on even where it is legal.
      for (const img of carousel.querySelectorAll('img[sizes]')) {
        img.setAttribute('sizes', '(min-width: 750px) 300px, 55vw');
      }

      // Cards rendered outside a section carry an empty section id, and a quick
      // add re-renders the section it reports. Point them at this one.
      if (sectionId) {
        for (const element of carousel.querySelectorAll('[data-section-id]')) {
          element.dataset.sectionId = sectionId;
        }
      }

      if (requestId !== this.#requestId) return;

      host.replaceChildren(carousel);
      this.hidden = false;
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;

      // Leave the shelf hidden. It is one of several recommendation rows on the
      // page, so an empty brand or a failed fetch should cost nothing visible.
      this.dataset.error = error instanceof Error ? error.message : String(error);
    } finally {
      if (requestId === this.#requestId) this.#activeFetch = null;
    }
  }
}

if (!customElements.get('pdp-brand-row')) {
  customElements.define('pdp-brand-row', PdpBrandRow);
}
